// ============================================================================
// AGM Notice — the report editor (ADR-0024; replaces the AGM Pack screen)
// ============================================================================
// Opened from the Reports tab (report code AGM_NOTICE), in the same document
// layout as the text notes and reports:
//
//   Left   — the Notice's parts: Notice details, Introduction, the business
//            (ordinary then special, numbered as printed), Notes. Add from the
//            AGM_PACK_SCH3 library, "Suggest from Corporate Facts", reorder.
//   Middle — the selected part's text, full height (a special business item
//            switches between its resolution and its s.102 statement).
//   Right  — Details (the item's type, proxy label, fields), Checklist,
//            Print (Notice / MGT-11 / attendance slip / full pack, PDF or Word).
//
// The data stays where it was: the Notice and the resolutions are Corporate
// Facts sections (notice, resolutions), saved by section — this editor never
// writes any other section. Printing reads the SAVED facts (AgmPackService).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useFsaCorporateFacts, parseCorporateFactsData } from 'hooks/api/fsa/useFsaCorporateFacts';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import { fsaEngagementSignaturePanelService } from 'api/services/fsa/fsaSignatureService';
import TiptapEditor from 'components/modals/modules/fsa/notes/panes/policy/TiptapEditor';
import { generateLocalId } from 'components/modals/modules/fsa/notes/panes/policy/SectionedNarrativeEditor';
import CorporateFactsSheet from 'components/modules/fsa/engagement/corporate-facts/CorporateFactsSheet';
import { fromMaster } from 'components/modules/fsa/engagement/corporate-facts/factsDirectors';
import { sectionSaveRequest } from 'components/modules/fsa/engagement/corporate-facts/factsSections';
import DocumentSidePanel, { readPanelPrefs, writePanelPrefs, type SidePanelTab } from './DocumentSidePanel';
import { FULLSCREEN_STYLE, FullscreenButton, useModalFullscreen } from './useModalFullscreen';
import {
  agendaNumbers, agmChecklist, makeResolution, moveWithinGroup, printedOrder, suggestAgenda, type PrefillContext,
} from './agmNotice';
import type { ID } from 'api/types';
import type { EngagementSignaturePanel } from 'api/types/fsa/signature.types';
import type {
  AgmLibraryEntry,
  AgmNotice,
  AgmResolution,
  CorporateFactsDirector,
} from 'api/types/fsa/corporateFacts.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  reportId: ID | null;
  reportHeader?: { code: string; name: string };
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (reportId: ID) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

type PrintDoc = 'notice' | 'proxy' | 'attendance' | 'all';
/** The editor's own sections — everything else in the facts is left alone. */
interface Draft { notice: AgmNotice; resolutions: AgmResolution[] }

const PRINT_DOCS: Array<[PrintDoc, string]> = [
  ['all', 'Full pack'],
  ['notice', 'Notice (with s.102 statement)'],
  ['proxy', 'Proxy form (MGT-11)'],
  ['attendance', 'Attendance slip'],
];
const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };
const isoDate = (s: string | null | undefined): string => (s ? s.slice(0, 10) : '');
const fmtDate = (s: string | null | undefined): string => {
  const d = isoDate(s);
  return d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '';
};

const AgmNoticeModal: React.FC<Props> = ({
  isOpen, engagementId, reportId, reportHeader, onClose, tabId, moduleName, onRestoreModal, minimizeRef,
}) => {
  const engId = String(engagementId);
  const { data: facts, loading, refetch } = useFsaCorporateFacts(isOpen ? engId : null);
  const factsData = useMemo(() => (facts ? parseCorporateFactsData(facts.data) : null), [facts]);

  const [draft, setDraft] = useState<Draft | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const snapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  // Bumped when saved text is loaded — TipTap reads its content only at creation.
  const [contentVersion, setContentVersion] = useState(0);
  const [selected, setSelected] = useState('details');
  const [textPart, setTextPart] = useState<'text' | 'explanatory'>('text');

  // ───── library · directors · signature panel ─────────────────────────────
  const [library, setLibrary] = useState<{ version: number; entries: AgmLibraryEntry[] } | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen || library) return;
    fsaCorporateFactsService.getAgmLibrary()
      .then(r => {
        let entries: AgmLibraryEntry[] = [];
        try { entries = JSON.parse(r.data?.entriesJson ?? '[]'); } catch { entries = []; }
        setLibrary({ version: r.data?.version ?? 0, entries });
      })
      .catch((err: any) => {
        const serverMessage = err?.response?.data?.message;
        setLibraryError(serverMessage
          ? `${serverMessage} Run the database migration (fixes/174) and reopen.`
          : 'The AGM Notice library could not be loaded.');
      });
  }, [isOpen, library]);

  const [masterDirectors, setMasterDirectors] = useState<CorporateFactsDirector[]>([]);
  const [panel, setPanel] = useState<EngagementSignaturePanel | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    fsaCorporateFactsService.getMasterDirectors(engId).then(list => setMasterDirectors(list.map(fromMaster))).catch(() => setMasterDirectors([]));
    fsaEngagementSignaturePanelService.getByEngagement(engId).then(setPanel).catch(() => setPanel(null));
  }, [isOpen, engId]);
  // The engagement's own directors list (Corporate Facts), in its order; the master's only until one is saved.
  const directors = useMemo(
    () => (factsData?.directors ?? masterDirectors).filter(d => !d.ceased),
    [factsData?.directors, masterDirectors],
  );
  const prefillCtx: PrefillContext = useMemo(
    () => ({ auditFirmName: panel?.auditFirmName, auditFirmRegistrationNumber: panel?.auditFirmRegistrationNumber }),
    [panel],
  );

  // ───── load (+ seed the Notice text from the library on first open) ──────
  useEffect(() => {
    if (!isOpen || !factsData || !library) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    const saved: Draft = { notice: factsData.notice ?? {}, resolutions: factsData.resolutions ?? [] };
    snapshotRef.current = JSON.stringify(saved);
    // Seeded AFTER the snapshot, so it reads as unsaved and the auditor saves
    // the text (the pack prints the saved facts).
    const notice: AgmNotice = { ...saved.notice };
    const intro = library.entries.find(e => e.kind === 'notice_intro');
    const notes = library.entries.find(e => e.kind === 'notice_notes');
    let seeded = false;
    if (notice.intro == null && intro?.body) { notice.intro = intro.body; seeded = true; }
    if (notice.notes == null && notes?.body) { notice.notes = notes.body; seeded = true; }
    if (seeded) notice.library_version = library.version;
    setDraft(seeded ? { ...saved, notice } : saved);
    setContentVersion(v => v + 1);
  }, [isOpen, factsData, library]);

  const isDirty = draft != null && JSON.stringify(draft) !== snapshotRef.current;
  const notice = useMemo(() => draft?.notice ?? {}, [draft]);
  const resolutions = useMemo(() => draft?.resolutions ?? [], [draft]);
  const numbers = useMemo(() => agendaNumbers(resolutions), [resolutions]);
  const current = resolutions.find(r => r.local_id === selected) ?? null;

  const patchNotice = useCallback((p: Partial<AgmNotice>) => {
    setDraft(prev => (prev ? { ...prev, notice: { ...prev.notice, ...p } } : prev));
  }, []);
  const setResolutions = useCallback((list: AgmResolution[]) => {
    setDraft(prev => (prev ? { ...prev, resolutions: list } : prev));
  }, []);
  const updateResolution = useCallback((id: string, p: Partial<AgmResolution>) => {
    setDraft(prev => (prev ? { ...prev, resolutions: prev.resolutions.map(r => (r.local_id === id ? { ...r, ...p } : r)) } : prev));
  }, []);

  const addFromLibrary = (code: string) => {
    const entry = library?.entries.find(e => e.kind === 'resolution' && e.code === code);
    if (!entry || !draft || !factsData || !library) return;
    const r = makeResolution(entry, library.version, factsData, resolutions, prefillCtx, generateLocalId);
    setResolutions([...resolutions, r]);
    setSelected(r.local_id);
    setTextPart('text');
  };
  const buildSuggested = () => {
    if (!library || !factsData) return;
    const out = suggestAgenda(library, factsData, resolutions, prefillCtx, generateLocalId);
    if (out.length === resolutions.length) {
      notificationService.toast.info('Nothing to add — the suggested items are already on the agenda.');
      return;
    }
    setResolutions(out);
    notificationService.toast.success(`${out.length - resolutions.length} item(s) added — review the text and Save.`);
  };
  const [removeId, setRemoveId] = useState<string | null>(null);
  const confirmRemove = () => {
    if (!removeId) return;
    setResolutions(resolutions.filter(r => r.local_id !== removeId));
    if (selected === removeId) setSelected('details');
    setRemoveId(null);
  };

  // ───── save ──────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const handleSave = useCallback(async () => {
    if (!draft || !facts || !factsData) return;
    setIsSaving(true);
    try {
      // Only the Notice and the agenda — never another screen's sections.
      await fsaCorporateFactsService.saveSections(engId, sectionSaveRequest(facts, { ...factsData, notice: draft.notice, resolutions: draft.resolutions }));
      snapshotRef.current = JSON.stringify(draft);
      notificationService.toast.success('AGM Notice saved.');
      isRestoreRef.current = true; // keep the open editors; the reload only refreshes stamps
      await refetch();
    } catch (err: any) {
      isRestoreRef.current = false;
      notificationService.toast.error(err?.response?.data?.message ?? 'Error saving the AGM Notice.');
    } finally {
      setIsSaving(false);
    }
  }, [draft, facts, factsData, engId, refetch]);

  // Closing (not minimising) forgets the draft, so the next open reads fresh.
  const closeAndReset = useCallback(() => {
    setDraft(null);
    snapshotRef.current = '';
    isRestoreRef.current = false;
    setSelected('details');
    onClose();
  }, [onClose]);
  const [discardOpen, setDiscardOpen] = useState(false);
  const handleClose = useCallback(() => {
    if (isDirty) { setDiscardOpen(true); return; }
    closeAndReset();
  }, [isDirty, closeAndReset]);

  // ───── print ─────────────────────────────────────────────────────────────
  const [printing, setPrinting] = useState<string | null>(null);
  const openPdf = async (doc: PrintDoc) => {
    setPrinting(`pdf:${doc}`);
    try {
      const blob = await fsaCorporateFactsService.getAgmPackPdf(engId, doc);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      notificationService.toast.error('Could not generate the PDF.');
    } finally {
      setPrinting(null);
    }
  };
  const downloadWord = async (doc: PrintDoc, label: string) => {
    setPrinting(`word:${doc}`);
    try {
      await fsaCorporateFactsService.downloadAgmPackDocx(engId, doc, `${label.replace(/[^A-Za-z0-9 -]/g, '').trim()}.docx`);
    } catch {
      notificationService.toast.error('Could not generate the Word document.');
    } finally {
      setPrinting(null);
    }
  };

  // ───── layout: full screen · side panel · Corporate Facts sheet ──────────
  const [fullscreen, toggleFullscreen] = useModalFullscreen();
  const prefs = useMemo(() => readPanelPrefs(), []);
  const [panelOpen, setPanelOpen] = useState(prefs.open);
  const [panelTab, setPanelTab] = useState(prefs.tab === 'checklist' || prefs.tab === 'print' ? prefs.tab : 'details');
  useEffect(() => { writePanelPrefs(panelOpen, panelTab); }, [panelOpen, panelTab]);
  const [factsSheetOpen, setFactsSheetOpen] = useState(false);

  // ───── minimise ──────────────────────────────────────────────────────────
  const { minimize } = useModalMinimize();
  const handleMinimize = useCallback(() => {
    if (!tabId || !reportId) return;
    const savedSnapshot = snapshotRef.current;
    const savedDraft = draftRef.current ? (JSON.parse(JSON.stringify(draftRef.current)) as Draft) : null;
    minimize({
      id: `${tabId}-fsa-report-${reportId}`,
      title: `${reportHeader?.code ?? 'AGM_NOTICE'} · ${reportHeader?.name ?? 'AGM Notice'}`,
      moduleName: moduleName ?? 'FSA',
      tabId,
      isDirty: savedDraft != null && JSON.stringify(savedDraft) !== savedSnapshot,
      onRestore: () => {
        if (savedDraft) {
          isRestoreRef.current = true;
          setDraft(savedDraft);
          snapshotRef.current = savedSnapshot;
          setContentVersion(v => v + 1);
        }
        onRestoreModal?.(reportId);
      },
    });
    onClose();
  }, [tabId, reportId, reportHeader, moduleName, minimize, onClose, onRestoreModal]);
  if (minimizeRef) minimizeRef.current = handleMinimize;

  const checks = useMemo(
    () => agmChecklist(notice, resolutions, { agmNumber: facts?.agmNumber, agmDate: facts?.agmDate, agmVenue: facts?.agmVenue }),
    [notice, resolutions, facts?.agmNumber, facts?.agmDate, facts?.agmVenue],
  );
  const openChecks = checks.filter(c => !c.ok);
  const blockers = openChecks.filter(c => c.level === 'block').length;

  if (!isOpen) return null;

  const goTo = (target?: string) => {
    if (!target) return;
    if (target === 'facts') { setFactsSheetOpen(true); return; }
    setSelected(target);
    setTextPart('text');
    if (target !== 'details' && target !== 'intro' && target !== 'notes') setPanelTab('details');
  };

  // ───── the middle: the selected part ─────────────────────────────────────
  const signatoryMode = notice.signatory?.director_id ? notice.signatory.director_id : (notice.signatory?.name ? '__other' : '');
  const renderMiddle = () => {
    if (selected === 'details') {
      return (
        <div style={formScrollStyle}>
          <div style={meetingCardStyle}>
            <div style={{ flex: 1 }}>
              <div style={miniLabelStyle}>The meeting (Corporate Facts)</div>
              {facts?.agmNumber || facts?.agmDate || facts?.agmVenue ? (
                <div style={{ fontSize: '0.85rem' }}>
                  {facts?.agmNumber ? `${facts.agmNumber}${ordinal(facts.agmNumber)} Annual General Meeting` : 'AGM number not set'}
                  {' · '}{facts?.agmDate ? fmtDate(facts.agmDate) : 'date not set'}
                  {facts?.agmTime ? ` at ${facts.agmTime}` : ''}
                  {' · '}{facts?.agmVenue || 'venue not set'}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: '#92400e' }}>AGM number, date and venue are not set yet.</div>
              )}
            </div>
            <button type="button" className="btn btn-secondary" style={smallBtnStyle} onClick={() => setFactsSheetOpen(true)}>
              Edit in Corporate Facts ↗
            </button>
          </div>

          <div className="form-grid-3">
            <div className="form-group">
              <label className="form-label">Date of notice</label>
              <input type="date" className="form-input" value={notice.notice_date ?? ''}
                onChange={e => patchNotice({ notice_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Place</label>
              <input type="text" className="form-input" value={notice.place ?? ''} maxLength={120}
                onChange={e => patchNotice({ place: e.target.value })} placeholder={panel?.place ?? 'e.g. Kanjikode'} />
            </div>
            <div className="form-group">
              <label className="form-label">Signed by (By order of the Board)</label>
              <select className="form-input" value={signatoryMode}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '') return patchNotice({ signatory: undefined });
                  if (v === '__other') return patchNotice({ signatory: { director_id: null, name: '', designation: 'Company Secretary', id_line: '' } });
                  const d = directors.find(x => x.director_id === v);
                  if (d) patchNotice({ signatory: { director_id: d.director_id, name: d.name, designation: d.designation ?? '', id_line: d.din ? `DIN: ${d.din}` : '' } });
                }}>
                <option value="">— choose —</option>
                {directors.map(d => <option key={d.director_id} value={d.director_id}>{d.name}{d.designation ? ` (${d.designation})` : ''}</option>)}
                <option value="__other">Other (e.g. Company Secretary)…</option>
              </select>
            </div>
          </div>
          {signatoryMode === '__other' && (
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" value={notice.signatory?.name ?? ''}
                  onChange={e => patchNotice({ signatory: { ...notice.signatory, name: e.target.value } })} />
              </div>
              <div className="form-group">
                <label className="form-label">Designation</label>
                <input className="form-input" value={notice.signatory?.designation ?? ''}
                  onChange={e => patchNotice({ signatory: { ...notice.signatory, designation: e.target.value } })} />
              </div>
              <div className="form-group">
                <label className="form-label">Membership / DIN line</label>
                <input className="form-input" value={notice.signatory?.id_line ?? ''} placeholder="e.g. ACS 12345"
                  onChange={e => patchNotice({ signatory: { ...notice.signatory, id_line: e.target.value } })} />
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Directions to the venue (route / landmark / map link)</label>
            <textarea className="form-textarea" rows={3} value={notice.directions ?? ''} maxLength={600}
              onChange={e => patchNotice({ directions: e.target.value })} />
          </div>
        </div>
      );
    }
    if (selected === 'intro' || selected === 'notes') {
      return (
        <div style={editorContainerStyle}>
          <TiptapEditor
            fill
            sectionKey={`${contentVersion}:${selected}`}
            initialContent={selected === 'intro' ? notice.intro : notice.notes}
            onChange={doc => patchNotice(selected === 'intro' ? { intro: doc } : { notes: doc })}
            readOnly={isSaving}
          />
        </div>
      );
    }
    if (!current) return <div style={emptyStyle}>Choose a part of the Notice on the left.</div>;
    const part = current.business === 'SPECIAL' ? textPart : 'text';
    return (
      <div style={editorContainerStyle}>
        <TiptapEditor
          fill
          sectionKey={`${contentVersion}:${current.local_id}:${part}`}
          initialContent={part === 'text' ? current.text : (current.explanatory ?? EMPTY_DOC)}
          onChange={doc => updateResolution(current.local_id, part === 'text' ? { text: doc } : { explanatory: doc })}
          readOnly={isSaving}
        />
      </div>
    );
  };

  const middleTitle = selected === 'details' ? 'Notice details'
    : selected === 'intro' ? 'Introduction'
      : selected === 'notes' ? 'Notes'
        : current ? `${numbers.get(current.local_id)}. ${current.title}` : '';

  // ───── right panel ───────────────────────────────────────────────────────
  const detailsContent = current ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="form-group">
        <label className="form-label">Business</label>
        <select className="form-input" value={current.business}
          onChange={e => updateResolution(current.local_id, { business: e.target.value as AgmResolution['business'] })}>
          <option value="ORDINARY">Ordinary business</option>
          <option value="SPECIAL">Special business (with s.102 statement)</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Resolution type</label>
        <select className="form-input" value={current.resolution_type}
          onChange={e => updateResolution(current.local_id, { resolution_type: e.target.value as AgmResolution['resolution_type'] })}>
          <option value="ORDINARY">Ordinary resolution</option>
          <option value="SPECIAL">Special resolution</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Proxy-form label</label>
        <input className="form-input" value={current.proxy_label}
          onChange={e => updateResolution(current.local_id, { proxy_label: e.target.value })}
          title="Tokens like {res.director_name} fill from the fields below" />
        <div style={subtleStyle}>Tokens like {'{res.director_name}'} fill from the fields below.</div>
      </div>
      {(current.param_specs ?? []).map(ps => (
        <div className="form-group" key={ps.key}>
          <label className="form-label">{ps.label}{ps.required ? ' *' : ''}</label>
          {ps.type === 'director' ? (
            <select className="form-input" value={directors.find(d => d.name === current.params[ps.key])?.director_id ?? ''}
              onChange={e => {
                const d = directors.find(x => x.director_id === e.target.value);
                updateResolution(current.local_id, { params: { ...current.params, [ps.key]: d?.name ?? '', din: d?.din ?? current.params.din ?? '' } });
              }}>
              <option value="">{current.params[ps.key] ? current.params[ps.key] : '— choose —'}</option>
              {directors.map(d => <option key={d.director_id} value={d.director_id}>{d.name}</option>)}
            </select>
          ) : (
            <input className="form-input" type={ps.type === 'date' ? 'date' : 'text'}
              value={ps.type === 'date' ? isoDate(current.params[ps.key]) : (current.params[ps.key] ?? '')}
              onChange={e => updateResolution(current.local_id, { params: { ...current.params, [ps.key]: e.target.value } })} />
          )}
        </div>
      ))}
      <div style={subtleStyle}>From the library: {current.library_code} v{current.library_version}. The text is this engagement's copy.</div>
    </div>
  ) : (
    <div style={subtleStyle}>
      {selected === 'details'
        ? 'The Notice date, place, signatory and directions print on the Notice; the meeting itself (number, date, time, venue) comes from Corporate Facts.'
        : 'Select an item of business to see its type, proxy-form label and fields here.'}
    </div>
  );

  const checklistContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {checks.map(c => (
        <button key={c.id} type="button" style={checkRowStyle} onClick={() => goTo(c.target)} disabled={!c.target}>
          <span style={{ color: c.ok ? '#15803d' : c.level === 'block' ? '#b91c1c' : '#b45309', flexShrink: 0 }}>
            {c.ok ? <IconSystem.check size={14} /> : <IconSystem.warning size={14} />}
          </span>
          <span style={{ flex: 1, textAlign: 'left' }}>
            {c.label}
            {c.hint && <span style={{ display: 'block', ...subtleStyle }}>{c.hint}</span>}
          </span>
        </button>
      ))}
    </div>
  );

  const printContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {isDirty && <div style={warnStyle}>Unsaved changes — save first; the documents print the saved Notice.</div>}
      {blockers > 0 && !isDirty && <div style={warnStyle}>{blockers} checklist item(s) open — the documents will show gaps.</div>}
      {PRINT_DOCS.map(([doc, label]) => (
        <div key={doc} style={printRowStyle}>
          <span style={{ flex: 1, fontSize: '0.82rem', fontWeight: doc === 'all' ? 700 : 500 }}>{label}</span>
          <button type="button" className={doc === 'all' ? 'btn btn-primary' : 'btn btn-secondary'} style={smallBtnStyle}
            disabled={isDirty || printing != null} onClick={() => openPdf(doc)} title="Open the PDF in a new tab">
            {printing === `pdf:${doc}` ? '…' : 'PDF'}
          </button>
          <button type="button" className="btn btn-secondary" style={smallBtnStyle}
            disabled={isDirty || printing != null} onClick={() => downloadWord(doc, label)} title="Download an editable Word copy">
            {printing === `word:${doc}` ? '…' : 'Word'}
          </button>
        </div>
      ))}
      <div style={subtleStyle}>The Reports list's Print prints the full pack.</div>
    </div>
  );

  const sideTabs: SidePanelTab[] = [
    { id: 'details', label: 'Details', short: 'D', content: detailsContent },
    { id: 'checklist', label: 'Checklist', short: 'C', count: openChecks.length, warn: blockers > 0, content: checklistContent },
    { id: 'print', label: 'Print', short: 'P', content: printContent },
  ];

  // ───── the left rail ─────────────────────────────────────────────────────
  const libraryResolutions = library?.entries.filter(e => e.kind === 'resolution') ?? [];
  const ordered = printedOrder(resolutions);
  const railItem = (id: string, label: React.ReactNode, extra?: React.ReactNode) => (
    <div key={id} style={{ ...railRowStyle, ...(selected === id ? railRowActiveStyle : {}) }}>
      <button type="button" style={railMainStyle} onClick={() => { setSelected(id); setTextPart('text'); }}>{label}</button>
      {extra}
    </div>
  );
  const businessItems = (special: boolean) => {
    const group = ordered.filter(r => (r.business === 'SPECIAL') === special);
    if (group.length === 0) return <div style={railEmptyStyle}>None</div>;
    return group.map((r, gi) => railItem(r.local_id, (
      <>
        <span style={railIndexStyle}>{numbers.get(r.local_id)}</span>
        <span style={railTitleStyle} title={r.title}>{r.title}</span>
      </>
    ), (
      <span style={railActionsStyle}>
        <button type="button" style={railIconStyle} disabled={gi === 0 || isSaving} title="Move up"
          onClick={() => setResolutions(moveWithinGroup(resolutions, r.local_id, -1))}><IconSystem.chevronUp size={12} /></button>
        <button type="button" style={railIconStyle} disabled={gi === group.length - 1 || isSaving} title="Move down"
          onClick={() => setResolutions(moveWithinGroup(resolutions, r.local_id, 1))}><IconSystem.chevronDown size={12} /></button>
        <button type="button" style={railIconStyle} disabled={isSaving} title="Remove"
          onClick={() => setRemoveId(r.local_id)}><IconSystem.trash size={12} /></button>
      </span>
    )));
  };

  const headerName = reportHeader?.name ?? 'AGM Notice';
  const ready = draft != null && library != null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: '92vh', ...(fullscreen ? FULLSCREEN_STYLE : {}) }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
            <IconSystem.calendar size={16} />
            <h2 className="modal-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {headerName}
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 500 }}>
                · {reportHeader?.code ?? 'AGM_NOTICE'}
              </span>
            </h2>
            {isDirty && <span style={unsavedPillStyle}>Unsaved</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <FullscreenButton full={fullscreen} onToggle={toggleFullscreen} disabled={isSaving} />
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ flex: 1, padding: 0, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
          {libraryError ? (
            <div style={emptyStyle}>{libraryError}</div>
          ) : !ready ? (
            <div style={emptyStyle}>{loading || !library ? 'Loading the AGM Notice…' : "Couldn't load the AGM Notice."}</div>
          ) : (
            <>
              {/* LEFT — the Notice's parts */}
              <nav style={railStyle} aria-label="Parts of the Notice">
                <div style={railHeaderStyle}>Notice</div>
                <div style={railBodyStyle}>
                  {railItem('details', <span style={railTitleStyle}>Notice details</span>)}
                  {railItem('intro', <span style={railTitleStyle}>Introduction</span>)}
                  <div style={railGroupStyle}>Ordinary business</div>
                  {businessItems(false)}
                  <div style={railGroupStyle}>Special business</div>
                  {businessItems(true)}
                  <div style={railGroupStyle}>&nbsp;</div>
                  {railItem('notes', <span style={railTitleStyle}>Notes</span>)}
                </div>
                <div style={railFooterStyle}>
                  <select className="form-input" style={{ fontSize: '0.8125rem' }} value="" disabled={isSaving}
                    onChange={e => { addFromLibrary(e.target.value); e.target.value = ''; }}>
                    <option value="">+ Add item of business…</option>
                    {libraryResolutions.map(e => <option key={e.code} value={e.code}>{e.title}</option>)}
                  </select>
                  <button type="button" className="btn btn-secondary" style={{ ...smallBtnStyle, width: '100%' }} onClick={buildSuggested}
                    disabled={isSaving}
                    title="Adoption of accounts, final dividend (if recorded) and regularisation of additional directors (if recorded)">
                    Suggest from Corporate Facts
                  </button>
                </div>
              </nav>

              {/* MIDDLE — the selected part */}
              <section style={middleStyle}>
                <div style={middleHeaderStyle}>
                  <h3 style={middleTitleStyle}>{middleTitle}</h3>
                  {current && (
                    <span style={current.business === 'SPECIAL' ? specialPillStyle : ordinaryPillStyle}>
                      {current.business === 'SPECIAL' ? 'Special business' : 'Ordinary business'}
                      {' · '}{current.resolution_type === 'SPECIAL' ? 'special resolution' : 'ordinary resolution'}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {current?.business === 'SPECIAL' && (
                    <span style={segmentStyle} role="tablist">
                      {(['text', 'explanatory'] as const).map(p => (
                        <button key={p} type="button" role="tab" aria-selected={textPart === p}
                          style={textPart === p ? segmentOnStyle : segmentBtnStyle} onClick={() => setTextPart(p)}>
                          {p === 'text' ? 'Resolution text' : 'Explanatory statement (s.102)'}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
                {renderMiddle()}
              </section>

              {/* RIGHT — details · checklist · print */}
              <DocumentSidePanel tabs={sideTabs} active={panelTab} onActive={setPanelTab} open={panelOpen} onOpenChange={setPanelOpen} />
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Close</button>
          <button className="btn btn-secondary" onClick={() => openPdf('all')} disabled={isSaving || isDirty || printing != null || !ready}
            title={isDirty ? 'Save first — the pack prints the saved Notice' : 'Open the full pack (Notice, MGT-11, attendance slip) as PDF'}>
            <IconSystem.print size={14} /> {printing === 'pdf:all' ? 'Preparing…' : 'Print pack'}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !isDirty}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={removeId != null}
        title="Remove item of business?"
        message={`Remove "${resolutions.find(r => r.local_id === removeId)?.title ?? ''}" from the agenda? Its text is removed from this Notice (the library keeps its own).`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmRemove}
        onCancel={() => setRemoveId(null)}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); closeAndReset(); }}
        onCancel={() => setDiscardOpen(false)}
      />
      <CorporateFactsSheet
        open={factsSheetOpen}
        engagementId={engId}
        section="company"
        onClose={() => setFactsSheetOpen(false)}
        onSaved={() => { isRestoreRef.current = true; void refetch(); }}
      />
    </>
  );
};

function ordinal(n: number): string {
  const t = n % 100;
  if (t >= 11 && t <= 13) return 'th';
  return ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
}

// ───── styles ──────────────────────────────────────────────────────────────

const railStyle: React.CSSProperties = {
  width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
  borderRight: '1px solid var(--border-color)', background: 'var(--bg-primary)',
};
const railHeaderStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
  fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4,
};
const railBodyStyle: React.CSSProperties = { flex: 1, overflow: 'auto', padding: '0.25rem 0' };
const railFooterStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6, padding: '0.5rem 0.75rem',
  borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
};
const railGroupStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem 0.2rem', fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.4,
};
const railRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', borderLeft: '3px solid transparent' };
const railRowActiveStyle: React.CSSProperties = { borderLeftColor: 'var(--primary-color, #2563eb)', background: 'var(--bg-secondary)' };
const railMainStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0, padding: '0.4rem 0.5rem 0.4rem 0.75rem',
  background: 'transparent', border: 'none', textAlign: 'left', fontSize: '0.8125rem', color: 'var(--text-primary)', cursor: 'pointer',
};
const railIndexStyle: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontSize: '0.72rem', color: 'var(--text-secondary)', minWidth: 18, textAlign: 'right' };
const railTitleStyle: React.CSSProperties = { flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const railActionsStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 1, paddingRight: '0.25rem' };
const railIconStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22,
  border: 'none', borderRadius: 4, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
};
const railEmptyStyle: React.CSSProperties = { padding: '0.2rem 0.75rem 0.2rem 2.4rem', fontSize: '0.78rem', color: 'var(--text-secondary)', fontStyle: 'italic' };
const middleStyle: React.CSSProperties = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-secondary)' };
const middleHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', minHeight: 40, boxSizing: 'border-box',
  padding: '0.5rem 0.875rem', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)',
};
const middleTitleStyle: React.CSSProperties = { margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' };
const editorContainerStyle: React.CSSProperties = {
  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0.625rem 0.875rem', overflow: 'hidden',
};
const formScrollStyle: React.CSSProperties = { flex: 1, minHeight: 0, overflow: 'auto', padding: '0.875rem 1rem', background: 'var(--bg-primary)' };
const meetingCardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '0.625rem 0.75rem', marginBottom: '0.875rem',
  border: '1px solid var(--border-color)', borderRadius: 8, background: 'var(--bg-secondary)',
};
const emptyStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem',
  color: 'var(--text-secondary)', fontSize: '0.9rem', textAlign: 'center',
};
const miniLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)', marginBottom: 2,
};
const subtleStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary)' };
const warnStyle: React.CSSProperties = { fontSize: '0.78rem', color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '6px 8px' };
const smallBtnStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.3rem 0.7rem' };
const checkRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 6,
  background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer',
};
const printRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 6,
};
const pill: React.CSSProperties = { padding: '1px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' };
const ordinaryPillStyle: React.CSSProperties = { ...pill, background: '#e0f2fe', color: '#075985' };
const specialPillStyle: React.CSSProperties = { ...pill, background: '#fae8ff', color: '#86198f' };
const unsavedPillStyle: React.CSSProperties = { ...pill, background: '#fef3c7', color: '#92400e' };
const segmentStyle: React.CSSProperties = { display: 'inline-flex', border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' };
const segmentBtnStyle: React.CSSProperties = {
  padding: '4px 10px', border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
};
const segmentOnStyle: React.CSSProperties = { ...segmentBtnStyle, background: 'var(--primary-color, #2563eb)', color: 'white' };

export default AgmNoticeModal;
